import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { BoardItem, BoardColumn, StorySummary, MissingSpecPlaceholder } from '../../types/board.js';
import type { AssigneeFilterType } from './board-constants.js';
import { isAgentAssignee } from './board-constants.js';
import type { OwnerFilterType } from '../../services/board-state.js';
import { BoardStateController } from '../../services/board-state.js';
import { AuthStateController } from '../../services/auth-state.js';
import { ReflectionStateController } from '../../services/reflection-state.js';
import * as executionApi from '../../services/execution-api.js';
import * as boardApi from '../../services/board-api.js';
import * as founderBoardApi from '../../services/founder-board-api.js';
import type { FounderTicket, FounderTicketStatus } from '../../services/founder-board-api.js';
import { statusLabel } from '../../services/founder-board-api.js';
import * as companyApi from '../../services/company-api.js';
import type { PendingStrategy, AgentActivitySnapshot, CeoInboxItem } from '../../services/company-api.js';
import { wsClient } from '../../services/websocket-client.js';
import * as specLinkApi from '../../services/spec-link-api.js';
import './cp-story-viewer.js';
import './cp-link-spec-dialog.js';
import './cp-execution-status-badge.js';
import './cp-preflight-questions-panel.js';
import './cp-plan-review-modal.js';
import '../company/cp-founder-ticket-card.js';
import '../company/cp-strategy-approval-card.js';
import './cp-ceo-inbox-tab.js';

interface ColumnDef {
  key: BoardColumn;
  label: string;
}

const COLUMNS: ColumnDef[] = [
  { key: 'backlog', label: 'Backlog' },
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'done', label: 'Done' },
];

interface TabDef {
  key: string;
  label: string;
  enabled: boolean;
}

const STORY_MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-6', label: 'Sonnet' },
  { value: 'claude-opus-4-6', label: 'Opus' },
  { value: 'claude-haiku-4-5-20251001', label: 'Haiku' },
] as const;

const FOUNDER_COLUMNS: FounderTicketStatus[] = ['open', 'waiting'];

const TABS: TabDef[] = [
  { key: 'my-tasks', label: 'Meine Tasks', enabled: true },
  { key: 'agent-approval', label: 'Agent (Freigabe)', enabled: true },
  { key: 'agent-working', label: 'Agent (arbeitet)', enabled: true },
  { key: 'ceo-inbox', label: 'CEO-Inbox', enabled: true },
];

@customElement('cp-control-board')
export class CpControlBoard extends LitElement {
  createRenderRoot() { return this; }

  private _boardState = new BoardStateController(this);
  private _authState = new AuthStateController(this);
  private _reflectionState = new ReflectionStateController(this);
  @state() private _activeTab = 'my-tasks';
  @state() private _reflectionListExpanded = false;
  @state() private _draggedItem: { id: string; sourceColumn: BoardColumn } | null = null;
  @state() private _dragOverColumn: BoardColumn | null = null;
  @state() private _showCreateModal = false;
  @state() private _showLinkSpecDialog = false;
  @state() private _linkSpecType: 'strategy' | 'goal' = 'goal';
  @state() private _linkSpecId = 0;
  @state() private _toast: string | null = null;
  @state() private _viewerStory: StorySummary | null = null;
  @state() private _viewerItem: BoardItem | null = null;
  @state() private _founderTickets: FounderTicket[] = [];
  @state() private _founderLoading = false;
  @state() private _pendingStrategies: PendingStrategy[] = [];
  @state() private _strategiesLoading = false;
  @state() private _agentActivity: AgentActivitySnapshot | null = null;
  @state() private _activityLoading = false;
  @state() private _ceoInboxItems: CeoInboxItem[] = [];
  @state() private _ceoInboxLoading = false;
  @state() private _warningTickets = new Set<string>();
  private _toastTimer: ReturnType<typeof setTimeout> | null = null;
  private _durationTimer: ReturnType<typeof setInterval> | null = null;
  private _wsHandler: ((e: Event) => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this._boardState.load();
    void this._reflectionState.load();
    void this._loadFounderTickets();
    void this._loadPendingStrategies();
    void this._loadCeoInboxItems();

    this._wsHandler = (e: Event) => {
      const msg = (e as CustomEvent<{ type: string; payload?: Record<string, unknown> }>).detail;
      if (msg.type === 'founder-ticket:created' || msg.type === 'founder-ticket:updated') {
        void this._loadFounderTickets();
      }
      if (msg.type === 'strategy:state-changed') {
        void this._loadPendingStrategies();
      }
      if (msg.type === 'agent-activity:changed') {
        this._agentActivity = (msg as CustomEvent<{ type: string; payload: AgentActivitySnapshot }>).detail.payload as AgentActivitySnapshot;
      }
      if (msg.type === 'execution:warning') {
        const ticketId = msg.payload?.ticketId as string | undefined;
        if (ticketId) {
          this._warningTickets = new Set([...this._warningTickets, ticketId]);
        }
      }
      if (msg.type === 'strategy:auto-escalated') {
        void this._loadCeoInboxItems();
      }
      if (msg.type === 'execution:completed' || msg.type === 'execution:failed' || msg.type === 'execution:started') {
        const ticketId = msg.payload?.ticketId as string | undefined;
        if (ticketId && this._warningTickets.has(ticketId)) {
          const next = new Set(this._warningTickets);
          next.delete(ticketId);
          this._warningTickets = next;
        }
      }
    };
    wsClient.addEventListener('ws-message', this._wsHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._toastTimer !== null) {
      clearTimeout(this._toastTimer);
    }
    if (this._durationTimer !== null) {
      clearInterval(this._durationTimer);
      this._durationTimer = null;
    }
    if (this._wsHandler) {
      wsClient.removeEventListener('ws-message', this._wsHandler);
      this._wsHandler = null;
    }
  }

  private async _loadFounderTickets(): Promise<void> {
    this._founderLoading = true;
    try {
      this._founderTickets = await founderBoardApi.listTickets();
    } catch {
      // Silently fail — tab just shows empty state
    } finally {
      this._founderLoading = false;
    }
  }

  private async _loadPendingStrategies(): Promise<void> {
    this._strategiesLoading = true;
    try {
      this._pendingStrategies = await companyApi.getPendingApprovalStrategies();
    } catch {
      // Silently fail
    } finally {
      this._strategiesLoading = false;
    }
  }

  private async _loadCeoInboxItems(): Promise<void> {
    this._ceoInboxLoading = true;
    try {
      const companies = await companyApi.listCompanies('active');
      const results = await Promise.all(
        companies.map(c =>
          companyApi.getCeoInboxItems(c.id, c.name).catch(() => [] as CeoInboxItem[])
        )
      );
      this._ceoInboxItems = results.flat();
    } catch {
      // Silently fail
    } finally {
      this._ceoInboxLoading = false;
    }
  }

  private get _actionableTicketCount(): number {
    return this._founderTickets.filter(t => t.status === 'open' || t.status === 'waiting').length
      + this._pendingStrategies.length;
  }

  private _founderTicketsForColumn(status: FounderTicketStatus): FounderTicket[] {
    return this._founderTickets.filter(t => t.status === status);
  }

  private _handleTicketUpdated(e: CustomEvent<{ ticket: FounderTicket }>): void {
    const updated = e.detail.ticket;
    this._founderTickets = this._founderTickets.map(t => (t.id === updated.id ? updated : t));
  }

  private _handleStrategyUpdated(): void {
    // Reload strategies — the updated one may no longer be pending
    void this._loadPendingStrategies();
  }

  private _showToast(message: string): void {
    this._toast = message;
    if (this._toastTimer !== null) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { this._toast = null; }, 3000);
  }

  private _onFilterChange(e: CustomEvent): void {
    const { project } = e.detail as { project: string | null };
    this._boardState.setFilterProject(project);
  }

  private _onOwnerFilterChange(e: CustomEvent): void {
    const { owner } = e.detail as { owner: OwnerFilterType };
    this._boardState.setFilterOwner(owner);
  }

  private _onAssigneeFilterChange(e: Event): void {
    const value = (e.target as HTMLSelectElement).value as AssigneeFilterType;
    this._boardState.setFilterAssigneeType(value);
  }

  private async _onModelChange(e: CustomEvent): Promise<void> {
    const { itemId, model } = e.detail as { itemId: string; model: string };
    const item = this._boardState.items.find(i => i.id === itemId);
    if (!item) return;

    // Optimistic UI update
    const previousModel = item.executionModel;
    item.executionModel = model;
    this.requestUpdate();

    try {
      await executionApi.setModel(itemId, model);
    } catch {
      // Revert on error
      item.executionModel = previousModel;
      this.requestUpdate();
      this._showToast('Model konnte nicht gespeichert werden');
    }
  }

  private _onItemSelect(e: CustomEvent): void {
    const { item } = e.detail as { item: BoardItem };
    if (item.sourceType === 'spec') {
      void this._boardState.toggleSpecExpanded(item);
      return;
    }
    this._boardState.selectItem(item);
  }

  private _onToggleChildren(e: Event, item: BoardItem): void {
    e.stopPropagation();
    void this._boardState.toggleExpand(item);
  }

  private _onOpenLinkSpecDialog(e: Event, item: BoardItem): void {
    e.stopPropagation();
    const parts = item.id.split(':');
    this._linkSpecType = item.sourceType as 'strategy' | 'goal';
    this._linkSpecId = Number(parts[2]);
    this._showLinkSpecDialog = true;
  }

  private _onLinkSpecDialogClose(): void {
    this._showLinkSpecDialog = false;
  }

  private _onLinkCreated(): void {
    this._showLinkSpecDialog = false;
    void this._boardState.refresh();
  }

  private async _onUnlinkSpec(e: Event, placeholder: MissingSpecPlaceholder): Promise<void> {
    e.stopPropagation();
    try {
      await specLinkApi.deleteLink(placeholder.linkId);
      void this._boardState.refresh();
    } catch {
      this._showToast('Link konnte nicht entfernt werden');
    }
  }

  private _onDetailClose(): void {
    this._boardState.clearSelection();
  }

  private _onDetailDeleted(): void {
    this._boardState.clearSelection();
    void this._boardState.refresh();
  }

  private _onTabClick(tab: TabDef): void {
    if (!tab.enabled) return;
    this._activeTab = tab.key;
    if (tab.key === 'agent-working') {
      void this._loadAgentActivity();
    }
    if (tab.key === 'ceo-inbox') {
      void this._loadCeoInboxItems();
    }
  }

  private _onCreateClick(): void {
    this._showCreateModal = true;
  }

  private _onCreateModalClose(): void {
    this._showCreateModal = false;
  }

  private _onBacklogItemCreated(): void {
    this._showCreateModal = false;
    this._showToast('Backlog-Item erstellt');
    void this._boardState.refresh();
  }

  private _onReflectionBadgeClick(): void {
    this._reflectionListExpanded = !this._reflectionListExpanded;
    if (this._reflectionListExpanded) {
      void this._reflectionState.load();
    }
  }

  // --- Drag-and-Drop ---

  private _onDragStart(e: DragEvent, item: BoardItem, sourceColumn: BoardColumn): void {
    e.dataTransfer?.setData('text/plain', item.id);
    this._draggedItem = { id: item.id, sourceColumn };
  }

  private _onDragOver(e: DragEvent, column: BoardColumn): void {
    e.preventDefault();
    this._dragOverColumn = column;
  }

  private _onDragLeave(e: DragEvent): void {
    // Only clear if leaving the column body itself (not entering a child)
    const related = e.relatedTarget as Node | null;
    const target = e.currentTarget as HTMLElement;
    if (!related || !target.contains(related)) {
      this._dragOverColumn = null;
    }
  }

  private async _onDrop(e: DragEvent, targetColumn: BoardColumn): Promise<void> {
    e.preventDefault();
    this._dragOverColumn = null;

    if (!this._draggedItem) return;

    const { id: itemId, sourceColumn } = this._draggedItem;
    this._draggedItem = null;

    if (sourceColumn === targetColumn) return;

    const item = this._boardState.items.find(i => i.id === itemId);
    if (!item) return;

    // Spec/backlog items are always agent-executed; for todos/goals check assignee
    const isAgentTask = item.sourceType === 'spec' || item.sourceType === 'backlog'
      || isAgentAssignee(item.assignee);

    // Agent tasks need a project or workspace path for execution
    if (isAgentTask && targetColumn === 'in_progress') {
      if (!item.sourceProjectId && !item.sourceProjectPath) {
        this._showToast('Projekt oder Workspace muss zugewiesen werden');
        return;
      }

      this._boardState.updateItemBoardColumn(itemId, targetColumn);

      const sourceType = item.sourceType === 'spec' ? 'specwright'
        : item.sourceType === 'todo' ? 'todo'
        : 'backlog';
      try {
        const model = await executionApi.getItemModel(itemId);
        // Start execution first (enqueues immediately) so enrichment covers reloads,
        // then persist status as fallback for items not yet picked up by the queue.
        await executionApi.startExecution(itemId, {
          projectId: item.sourceProjectId || undefined,
          projectPath: item.sourceProjectPath,
          sourceType,
          model,
        });
        void boardApi.updateBoardItemStatus(itemId, targetColumn);
      } catch (err) {
        this._boardState.updateItemBoardColumn(itemId, sourceColumn);
        const message = err instanceof Error ? err.message : 'Execution konnte nicht gestartet werden';
        this._showToast(message);
      }
    } else if (isAgentTask && sourceColumn === 'in_progress') {
      // Stop execution + persist status
      this._boardState.updateItemBoardColumn(itemId, targetColumn);
      try { await executionApi.stopExecution(itemId); } catch { /* ignore */ }
      try {
        await boardApi.updateBoardItemStatus(itemId, targetColumn);
      } catch {
        this._boardState.updateItemBoardColumn(itemId, sourceColumn);
      }
    } else {
      // User-tasks or non-execution moves: only status update
      this._boardState.updateItemBoardColumn(itemId, targetColumn);
      try {
        await boardApi.updateBoardItemStatus(itemId, targetColumn);
      } catch {
        this._boardState.updateItemBoardColumn(itemId, sourceColumn);
      }
    }
  }

  // --- Render helpers ---

  private _renderTabs() {
    const ticketCount = this._actionableTicketCount;

    return html`
      <div class="cp-control-board__tabs">
        ${TABS.map(tab => html`
          <button
            class="cp-control-board__tab ${this._activeTab === tab.key ? 'cp-control-board__tab--active' : ''} ${!tab.enabled ? 'cp-control-board__tab--disabled' : ''}"
            @click=${() => this._onTabClick(tab)}
            ?disabled=${!tab.enabled}
          >
            ${tab.label}
            ${!tab.enabled ? html`<span class="cp-control-board__tab-badge">Coming Soon</span>` : nothing}
            ${tab.key === 'agent-approval' && ticketCount > 0
              ? html`<span class="cp-control-board__tab-count">${ticketCount}</span>`
              : nothing}
            ${tab.key === 'ceo-inbox' && this._ceoInboxItems.length > 0
              ? html`<span class="cp-control-board__tab-count">${this._ceoInboxItems.length}</span>`
              : nothing}
          </button>
        `)}
      </div>
    `;
  }

  private _renderToolbar() {
    return html`
      <div class="cp-control-board__toolbar">
        <cp-board-filter
          .projects=${this._boardState.projects}
          .selected=${this._boardState.filterProject}
          .users=${this._boardState.users}
          .selectedOwner=${this._boardState.filterOwner}
          @filter-change=${this._onFilterChange}
          @owner-filter-change=${this._onOwnerFilterChange}
        ></cp-board-filter>
        <button class="cp-control-board__add-btn" @click=${this._onCreateClick} title="Backlog-Item erstellen">+</button>
        <cp-reflection-badge
          @reflection-badge-click=${this._onReflectionBadgeClick}
        ></cp-reflection-badge>
        <select
          class="cp-control-board__assignee-filter"
          .value=${this._boardState.filterAssigneeType}
          @change=${this._onAssigneeFilterChange}
        >
          <option value="all">Alle Tasks</option>
          <option value="user">Meine Tasks</option>
          <option value="agent">Agent-Tasks</option>
        </select>
      </div>
      <cp-reflection-list
        .notes=${this._reflectionState.notes}
        ?expanded=${this._reflectionListExpanded}
      ></cp-reflection-list>
    `;
  }

  private _renderColumn(col: ColumnDef, items: BoardItem[]) {
    const currentUserId = this._authState.currentUser?.id;
    const isDragOver = this._dragOverColumn === col.key;

    return html`
      <div class="cp-control-board__column">
        <div class="cp-control-board__column-header">
          <span class="cp-control-board__column-title">${col.label}</span>
          <span class="cp-control-board__column-count">${items.length}</span>
        </div>
        <div
          class="cp-control-board__column-body ${isDragOver ? 'cp-control-board__column-body--drag-over' : ''}"
          @dragover=${(e: DragEvent) => this._onDragOver(e, col.key)}
          @dragleave=${(e: DragEvent) => this._onDragLeave(e)}
          @drop=${(e: DragEvent) => this._onDrop(e, col.key)}
        >
          ${items.length > 0
            ? items.map(item => {
                const hasLinkedSpecs = (item.children?.length ?? 0) > 0 || (item.missing?.length ?? 0) > 0;
                const childrenExpanded = this._boardState.isChildrenExpanded(item.id);
                const childCount = (item.children?.length ?? 0) + (item.missing?.length ?? 0);
                return html`
                  <div
                    class="cp-control-board__drag-item"
                    draggable="true"
                    @dragstart=${(e: DragEvent) => this._onDragStart(e, item, col.key)}
                  >
                    <cp-board-card
                      .item=${item}
                      ?readonly=${!this._boardState.isOwnItem(item, currentUserId)}
                      ?expanded=${item.sourceType === 'spec' && this._boardState.isSpecExpanded(item.id)}
                      ?showExecutionBadge=${col.key === 'in_progress' || col.key === 'todo'}
                    ></cp-board-card>
                    <cp-preflight-questions-panel .ticketId=${item.id}></cp-preflight-questions-panel>
                  </div>
                  ${item.sourceType === 'spec' && this._boardState.isSpecExpanded(item.id)
                    ? this._renderStoryList(item)
                    : nothing}
                  <div class="cp-control-board__spec-link-row">
                    ${hasLinkedSpecs ? html`
                      <button
                        class="cp-control-board__children-toggle ${childrenExpanded ? 'cp-control-board__children-toggle--expanded' : ''}"
                        @click=${(e: Event) => this._onToggleChildren(e, item)}
                      >
                        <span class="cp-control-board__children-toggle-icon">${childrenExpanded ? '▼' : '▶'}</span>
                        ${childCount} Spec${childCount !== 1 ? 's' : ''}
                      </button>
                    ` : nothing}
                    ${item.sourceType === 'goal' ? html`
                      <button
                        class="cp-control-board__link-spec-btn"
                        @click=${(e: Event) => this._onOpenLinkSpecDialog(e, item)}
                        title="Spec verlinken"
                      >+ Spec</button>
                    ` : nothing}
                  </div>
                  ${childrenExpanded ? this._renderLinkedSpecCards(item) : nothing}
                `;
              })
            : html`<div class="cp-control-board__column-empty">Keine Items</div>`
          }
        </div>
      </div>
    `;
  }

  private _renderStoryList(item: BoardItem) {
    if (this._boardState.isSpecStoriesLoading(item.id)) {
      return html`<div class="cp-control-board__story-list">
        <span class="cp-control-board__story-list-loading">Lade Stories...</span>
      </div>`;
    }

    const stories = this._boardState.getSpecStories(item.id);
    if (!stories?.length) return nothing;

    return html`
      <div class="cp-control-board__story-list">
        ${stories.map(story => html`
          <div
            class="cp-control-board__story-item"
            @click=${(e: Event) => this._onStoryClick(e, item, story)}
          >
            <span class="cp-control-board__story-status cp-control-board__story-status--${story.status}"></span>
            <span class="cp-control-board__story-title">${story.title}</span>
            ${story.type ? html`<span class="cp-control-board__story-type">${story.type}</span>` : nothing}
            ${story.priority ? html`<span class="cp-control-board__story-priority cp-control-board__story-priority--${story.priority}">${story.priority}</span>` : nothing}
            <select
              class="cp-control-board__story-model-select"
              .value=${story.model ?? item.executionModel ?? 'claude-sonnet-4-6'}
              @change=${(e: Event) => void this._onStoryModelChange(e, item, story)}
              @click=${(e: Event) => e.stopPropagation()}
            >
              ${STORY_MODEL_OPTIONS.map(opt => html`
                <option value=${opt.value} ?selected=${(story.model ?? item.executionModel ?? 'claude-sonnet-4-6') === opt.value}>${opt.label}</option>
              `)}
            </select>
          </div>
        `)}
      </div>
    `;
  }

  private _renderLinkedSpecCards(item: BoardItem) {
    const children = item.children ?? [];
    const missing = item.missing ?? [];
    if (!children.length && !missing.length) return nothing;

    return html`
      <div class="cp-control-board__spec-children">
        ${children.map(spec => html`
          <div class="cp-control-board__spec-child">
            <div class="cp-control-board__spec-child-header">
              <span class="cp-control-board__spec-child-name">${spec.name}</span>
              <span class="cp-control-board__spec-child-counts">${spec.storyCounts.done}/${spec.storyCounts.total}</span>
              <span class="cp-control-board__spec-child-status cp-control-board__spec-child-status--${spec.status}">
                ${spec.status}
              </span>
            </div>
            ${spec.progressPercent > 0 ? html`
              <div class="cp-control-board__spec-child-bar">
                <div class="cp-control-board__spec-child-bar-fill" style="width:${spec.progressPercent}%"></div>
              </div>
            ` : nothing}
          </div>
        `)}
        ${missing.map(m => html`
          <div class="cp-control-board__spec-child cp-control-board__spec-child--missing">
            <span class="cp-control-board__spec-child-missing-label">Spec nicht verfügbar (${m.specId})</span>
            <button
              class="cp-control-board__spec-child-unlink"
              @click=${(e: Event) => void this._onUnlinkSpec(e, m)}
              title="Verlinkung entfernen"
            >×</button>
          </div>
        `)}
      </div>
    `;
  }

  private _onStoryClick(e: Event, item: BoardItem, story: StorySummary): void {
    e.stopPropagation();
    this._viewerStory = story;
    this._viewerItem = item;
  }

  private _onViewerClose(): void {
    this._viewerStory = null;
    this._viewerItem = null;
  }

  private async _onViewerModelChange(e: CustomEvent): Promise<void> {
    const { storyId, model } = e.detail as { storyId: string; model: string };
    if (!this._viewerItem) return;

    const parts = this._viewerItem.id.split(':');
    if (parts.length < 3 || parts[0] !== 'spec') return;
    const projectId = Number(parts[1]);
    const specId = parts.slice(2).join(':');

    // Optimistic UI update in expanded stories cache
    const stories = this._boardState.getSpecStories(this._viewerItem.id);
    const story = stories?.find(s => s.id === storyId);
    if (story) {
      story.model = model;
      this.requestUpdate();
    }
    // Also update viewer story
    if (this._viewerStory?.id === storyId) {
      this._viewerStory = { ...this._viewerStory, model };
    }

    try {
      await boardApi.updateStoryModel(projectId, specId, storyId, model);
    } catch {
      // Revert
      if (story) {
        story.model = undefined;
        this.requestUpdate();
      }
      this._showToast('Model konnte nicht gespeichert werden');
    }
  }

  private async _onStoryModelChange(e: Event, item: BoardItem, story: StorySummary): Promise<void> {
    e.stopPropagation();
    const model = (e.target as HTMLSelectElement).value;

    const parts = item.id.split(':');
    if (parts.length < 3 || parts[0] !== 'spec') return;
    const projectId = Number(parts[1]);
    const specId = parts.slice(2).join(':');

    // Optimistic UI update
    const previousModel = story.model;
    story.model = model;
    this.requestUpdate();

    try {
      await boardApi.updateStoryModel(projectId, specId, story.id, model);
    } catch {
      story.model = previousModel;
      this.requestUpdate();
      this._showToast('Model konnte nicht gespeichert werden');
    }
  }

  private _renderFounderColumn(status: FounderTicketStatus) {
    const tickets = this._founderTicketsForColumn(status);

    return html`
      <div class="cp-control-board__column">
        <div class="cp-control-board__column-header">
          <span class="cp-control-board__column-title">${statusLabel(status)}</span>
          ${tickets.length > 0
            ? html`<span class="cp-control-board__column-count">${tickets.length}</span>`
            : nothing}
        </div>
        <div class="cp-control-board__column-body">
          ${tickets.length > 0
            ? tickets.map(ticket => html`
                <cp-founder-ticket-card
                  .ticket=${ticket}
                  @ticket-updated=${(e: CustomEvent<{ ticket: FounderTicket }>) => this._handleTicketUpdated(e)}
                ></cp-founder-ticket-card>
              `)
            : html`<div class="cp-control-board__column-empty">Keine Tickets</div>`
          }
        </div>
      </div>
    `;
  }

  private _renderPendingStrategies() {
    if (this._pendingStrategies.length === 0) return nothing;

    return html`
      <div class="cp-control-board__approval-section">
        <h3 class="cp-control-board__section-title">Strategie-Freigaben</h3>
        <div class="cp-control-board__strategy-list">
          ${this._pendingStrategies.map(s => html`
            <cp-strategy-approval-card
              .strategy=${s}
              @strategy-updated=${() => this._handleStrategyUpdated()}
            ></cp-strategy-approval-card>
          `)}
        </div>
      </div>
    `;
  }

  private _renderFounderTickets() {
    const isLoading = (this._founderLoading && this._founderTickets.length === 0)
      && (this._strategiesLoading && this._pendingStrategies.length === 0);

    if (isLoading) {
      return html`<div class="cp-control-board__loading">Lade Tickets...</div>`;
    }

    const hasActionable = this._actionableTicketCount > 0;

    if (!hasActionable) {
      return html`
        <div class="cp-control-board__empty">
          <p class="cp-control-board__empty-text">Keine offenen Tickets</p>
          <p class="cp-control-board__empty-sub">Tickets und Strategie-Freigaben erscheinen hier, wenn Agenten Genehmigungen oder Rueckfragen erstellen.</p>
        </div>
      `;
    }

    const hasFounderTickets = this._founderTickets.filter(t => t.status === 'open' || t.status === 'waiting').length > 0;

    return html`
      ${this._renderPendingStrategies()}
      ${hasFounderTickets ? html`
        <div class="cp-control-board__approval-section">
          <h3 class="cp-control-board__section-title">Founder Tickets</h3>
          <div class="cp-control-board__founder-grid">
            ${FOUNDER_COLUMNS.map(status => this._renderFounderColumn(status))}
          </div>
        </div>
      ` : nothing}
    `;
  }

  private async _loadAgentActivity(): Promise<void> {
    this._activityLoading = true;
    try {
      this._agentActivity = await companyApi.getAgentActivity();
      this._startDurationTimer();
    } catch {
      // Silently fail
    } finally {
      this._activityLoading = false;
    }
  }

  private _startDurationTimer(): void {
    if (this._durationTimer !== null) clearInterval(this._durationTimer);
    this._durationTimer = setInterval(() => {
      if (this._activeTab !== 'agent-working') {
        if (this._durationTimer !== null) {
          clearInterval(this._durationTimer);
          this._durationTimer = null;
        }
        return;
      }
      // Force re-render to update duration display
      this.requestUpdate();
    }, 1000);
  }

  private _formatDuration(startedAt: string): string {
    const ms = Date.now() - new Date(startedAt).getTime();
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}m ${sec.toString().padStart(2, '0')}s`;
  }

  private _stateIndicator(state: string) {
    switch (state) {
      case 'active': return html`<span class="cp-control-board__state-dot cp-control-board__state-dot--active"></span>`;
      case 'stuck': return html`<span class="cp-control-board__state-dot cp-control-board__state-dot--stuck"></span>`;
      case 'queued': return html`<span class="cp-control-board__state-dot cp-control-board__state-dot--queued"></span>`;
      case 'blocked': return html`<span class="cp-control-board__state-dot cp-control-board__state-dot--blocked"></span>`;
      default: return html`<span class="cp-control-board__state-dot cp-control-board__state-dot--idle"></span>`;
    }
  }

  private _actionLabel(action: string | null): string {
    if (!action) return '';
    const labels: Record<string, string> = {
      'processing': 'Verarbeitung',
      'start-strategizing': 'Strategieentwicklung',
      'strategy-review': 'Strategie-Review',
      'ceo-work-cycle': 'CEO Work Cycle',
      'executing': 'Ausfuehrung',
      'reporting': 'Berichterstellung',
      'ceo-ticket-response': 'Ticket-Bearbeitung',
    };
    return labels[action] ?? action;
  }

  private _renderAgentWorking() {
    if (this._activityLoading && !this._agentActivity) {
      return html`<div class="cp-control-board__loading">Lade Agent-Aktivitaet...</div>`;
    }

    if (!this._agentActivity || this._agentActivity.agents.length === 0) {
      return html`
        <div class="cp-control-board__empty">
          <p class="cp-control-board__empty-text">Keine aktiven Agents</p>
          <p class="cp-control-board__empty-sub">Agents erscheinen hier sobald sie in einer Company aktiv sind.</p>
        </div>
      `;
    }

    const { agents, claudeQueue, lastPollAt } = this._agentActivity;
    const activeAgents = agents.filter(a => a.state === 'active');
    const activeNames = activeAgents.map(a => a.agentName).join(', ');

    return html`
      <div class="cp-control-board__activity-summary">
        <span>Claude: ${claudeQueue.active}/${claudeQueue.maxConcurrency} aktiv${claudeQueue.waiting > 0 ? html` | ${claudeQueue.waiting} wartend` : nothing}${activeNames ? html` <span class="cp-control-board__activity-active-names">${activeNames}</span>` : nothing}</span>
        ${lastPollAt ? html`<span class="cp-control-board__activity-poll">Letzter Poll: ${new Date(lastPollAt).toLocaleTimeString('de-DE')}</span>` : nothing}
      </div>
      <div class="cp-control-board__activity-grid">
        ${agents.map(agent => html`
          <div class="cp-control-board__agent-card cp-control-board__agent-card--${agent.state}">
            <div class="cp-control-board__agent-card-header">
              ${agent.color ? html`<span class="cp-control-board__agent-color" style="background:${agent.color}"></span>` : nothing}
              ${this._stateIndicator(agent.state)}
              <span class="cp-control-board__agent-name">${agent.agentName}</span>
              <span class="cp-control-board__agent-role">${agent.role}</span>
              ${agent.state === 'active' ? html`<span class="cp-control-board__agent-active-badge">AKTIV</span>` : nothing}
            </div>
            ${agent.currentAction ? html`<div class="cp-control-board__agent-action">${this._actionLabel(agent.currentAction)}</div>` : nothing}
            ${agent.goalTitle ? html`<div class="cp-control-board__agent-goal">${agent.goalTitle}</div>` : nothing}
            ${agent.startedAt ? html`<div class="cp-control-board__agent-duration">seit ${this._formatDuration(agent.startedAt)}</div>` : nothing}
            ${agent.stuckThresholdExceeded ? html`<div class="cp-control-board__agent-stuck-warn">Moeglicherweise haengt dieser Agent</div>` : nothing}
          </div>
        `)}
      </div>
    `;
  }

  private _renderWarningBanner() {
    if (this._warningTickets.size === 0) return nothing;
    const warningItems = this._boardState.items.filter(i => this._warningTickets.has(i.id));
    if (!warningItems.length) return nothing;
    return html`
      <div class="cp-control-board__warning-banner">
        <span class="cp-control-board__warning-banner-icon">⚠</span>
        <span class="cp-control-board__warning-banner-label">Approaching Timeout:</span>
        ${warningItems.map(i => html`<span class="cp-control-board__warning-banner-item">${i.title}</span>`)}
      </div>
    `;
  }

  private _renderCeoInbox() {
    return html`
      <cp-ceo-inbox-tab
        .items=${this._ceoInboxItems}
        ?loading=${this._ceoInboxLoading}
        @ceo-inbox-reload=${() => void this._loadCeoInboxItems()}
      ></cp-ceo-inbox-tab>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="cp-control-board__empty">
        <p class="cp-control-board__empty-text">Keine Projekte registriert</p>
      </div>
    `;
  }

  private _renderMyTasks() {
    const { loadingState, error } = this._boardState;

    if (loadingState === 'loading' && this._boardState.items.length === 0) {
      return html`<div class="cp-control-board__loading">Lade Board...</div>`;
    }

    if (error) {
      return html`<div class="cp-control-board__error">${error}</div>`;
    }

    if (loadingState === 'loaded' && this._boardState.items.length === 0) {
      return this._renderEmpty();
    }

    return html`
      ${this._renderToolbar()}
      ${this._renderWarningBanner()}
      <div class="cp-control-board__grid" @board-item-select=${this._onItemSelect} @board-model-change=${this._onModelChange}>
        ${COLUMNS.map(col => this._renderColumn(col, this._boardState.itemsByColumn(col.key)))}
      </div>
      ${this._boardState.selectedItem
        ? html`<cp-board-detail
            .item=${this._boardState.selectedItem}
            @board-detail-close=${this._onDetailClose}
            @board-detail-deleted=${this._onDetailDeleted}
          ></cp-board-detail>`
        : nothing}
      <cp-board-create-modal
        ?open=${this._showCreateModal}
        @modal-closed=${this._onCreateModalClose}
        @backlog-item-created=${this._onBacklogItemCreated}
      ></cp-board-create-modal>
      <cp-link-spec-dialog
        ?open=${this._showLinkSpecDialog}
        .linkType=${this._linkSpecType}
        .linkId=${this._linkSpecId}
        @dialog-closed=${this._onLinkSpecDialogClose}
        @link-created=${this._onLinkCreated}
      ></cp-link-spec-dialog>
      ${this._viewerStory && this._viewerItem
        ? html`<cp-story-viewer
            .projectId=${Number(this._viewerItem.id.split(':')[1])}
            .specId=${this._viewerItem.id.split(':').slice(2).join(':')}
            .story=${this._viewerStory}
            .specModel=${this._viewerItem.executionModel ?? ''}
            @story-viewer-close=${this._onViewerClose}
            @story-model-change=${this._onViewerModelChange}
          ></cp-story-viewer>`
        : nothing}
    `;
  }

  render() {
    return html`
      ${this._renderTabs()}
      ${this._activeTab === 'my-tasks' ? this._renderMyTasks() : nothing}
      ${this._activeTab === 'agent-approval' ? this._renderFounderTickets() : nothing}
      ${this._activeTab === 'agent-working' ? this._renderAgentWorking() : nothing}
      ${this._activeTab === 'ceo-inbox' ? this._renderCeoInbox() : nothing}
      ${this._toast
        ? html`<div class="cp-control-board__toast">${this._toast}</div>`
        : nothing}
      <cp-plan-review-modal></cp-plan-review-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-control-board': CpControlBoard;
  }
}
